import { useState, useEffect, useRef } from 'react';
import { LuSend, LuPlay, LuRotateCcw, LuBot, LuTriangleAlert, LuCheck } from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import './TestAgentChat.css';
import { AgentTesterService } from '../services/AgentTesterService';
import hubService from '../hub/HubConnectionService';
import { useGlobalNotification } from '../contexts/GlobalNotificationContext';

interface TestMessage {
    id: string;
    text: string;
    sender: 'user' | 'agent';
    timestamp: Date;
}

interface Props {
    /** Agente aberto na tela. Sem isto a API cai no padrão da empresa. */
    agenteId?: number | null;
    /** Nome do agente aberto, só para comparar com quem de fato respondeu. */
    agenteNome?: string;
}

export const TestAgentChat = ({ agenteId, agenteNome }: Props) => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<TestMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isSessionStarting, setIsSessionStarting] = useState(false);
    /* Quem de fato atendeu. A API pode cair no padrão da empresa quando não
       recebe agenteId — e aí o que você lê na tela não é o que está respondendo. */
    const [quemAtende, setQuemAtende] = useState<{ id?: number; nome?: string } | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { isHubConnected } = useGlobalNotification();

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    };

    useEffect(() => { scrollToBottom(); }, [messages, isTyping]);

    /* Trocou de agente: a sessão anterior era de outro. Começa do zero. */
    useEffect(() => {
        setSessionId(null);
        setMessages([]);
        setQuemAtende(null);
    }, [agenteId]);

    useEffect(() => {
        const handleNewMessage = (sessionIdResponse: string, message: string) => {
            if (sessionIdResponse === sessionId) {
                setIsTyping(false);
                setMessages(prev => [...prev, {
                    id: Date.now().toString(), text: message, sender: 'agent', timestamp: new Date(),
                }]);
            }
        };

        if (isHubConnected && sessionId) {
            hubService.on('ReceiveTestMessage', handleNewMessage);
            return () => { hubService.off('ReceiveTestMessage', handleNewMessage); };
        }
    }, [isHubConnected, sessionId]);

    const handleStartSession = async () => {
        setIsSessionStarting(true);
        try {
            const response = await AgentTesterService.startSession(agenteId ?? undefined);
            if (response.sessionId) {
                setSessionId(response.sessionId);
                setQuemAtende({ id: response.agenteId, nome: response.agentName });
                setMessages([{
                    id: 'welcome',
                    text: response.mensagem || 'Sessão de teste iniciada! Como posso ajudar?',
                    sender: 'agent',
                    timestamp: new Date(),
                }]);
            } else {
                aviso.erro(response.mensagem || 'Não foi possível iniciar a sessão.');
            }
        } catch (error: any) {
            aviso.erro(textoDoErro(error, 'Não foi possível iniciar os testes.'));
        } finally {
            setIsSessionStarting(false);
        }
    };

    const handleResetSession = async () => {
        if (!sessionId) return;
        try {
            await AgentTesterService.resetSession({ sessionId, agenteId: agenteId ?? undefined });
            setSessionId(null);
            setMessages([]);
            setQuemAtende(null);
        } catch (error: any) {
            aviso.erro(textoDoErro(error, 'Não foi possível reiniciar a sessão.'));
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !sessionId) return;

        const currentInput = inputValue;
        setInputValue('');
        setMessages(prev => [...prev, {
            id: Date.now().toString(), text: currentInput, sender: 'user', timestamp: new Date(),
        }]);
        setIsTyping(true);

        try {
            const response = await AgentTesterService.sendMessage({
                sessionId, message: currentInput, agenteId: agenteId ?? undefined,
            });

            if (response && response.resposta) {
                setIsTyping(false);
                setMessages(prev => {
                    // A IA separa assunto por linha em branco; cada bloco vira um balão.
                    const blocks = response.resposta
                        .split('\n\n')
                        .map((t: string) => t.trim())
                        .filter((t: string) => t.length > 0);

                    return [...prev, ...blocks.map((blockText: string, index: number) => ({
                        id: Date.now().toString() + '-' + index,
                        text: blockText,
                        sender: 'agent' as const,
                        timestamp: new Date(),
                    }))];
                });
            }
        } catch (error: any) {
            setIsTyping(false);
            aviso.erro('Não foi possível enviar a mensagem.');
            console.error(error);
        }
    };

    /* O aviso que faltava: dizer com quem você está falando, e avisar quando
       não for quem você abriu. */
    const outroAgente = !!(quemAtende?.id && agenteId && quemAtende.id !== agenteId);

    return (
        <aside className="ta">
            <div className="ta-head">
                <div className="ta-head-id">
                    <span className="ta-title">Ensaio</span>
                    <span className="ta-sub">
                        {quemAtende?.nome
                            ? <>conversando com <b>{quemAtende.nome}</b></>
                            : 'converse como se fosse o cliente'}
                    </span>
                </div>
                {sessionId && (
                    <button type="button" className="ta-btn" onClick={handleResetSession}>
                        <LuRotateCcw size={13} /> Reiniciar
                    </button>
                )}
            </div>

            {quemAtende && (
                <div className={`ta-marca ${outroAgente ? 'is-warn' : 'is-ok'}`}>
                    {outroAgente
                        ? <><LuTriangleAlert size={13} /> Quem respondeu foi <b>{quemAtende.nome}</b>, o padrão
                            da empresa — não o agente aberto{agenteNome ? ` (${agenteNome})` : ''}.</>
                        : <><LuCheck size={13} /> É o agente que você está editando.</>}
                </div>
            )}

            {!sessionId ? (
                <div className="ta-vazio">
                    <div className="ta-vazio-icone"><LuBot size={20} /></div>
                    <p className="ta-vazio-texto">
                        O ensaio usa o que está <b>salvo</b>. Salve antes de testar uma mudança.
                    </p>
                    <button type="button" className="ta-btn is-primary"
                        onClick={handleStartSession} disabled={isSessionStarting}>
                        <LuPlay size={13} /> {isSessionStarting ? 'Iniciando…' : 'Iniciar ensaio'}
                    </button>
                </div>
            ) : (
                <>
                    <div className="ta-corpo">
                        {messages.map(msg => (
                            <div key={msg.id} className={`ta-msg is-${msg.sender}`}>
                                <div className="ta-bolha">{msg.text}</div>
                                <span className="ta-hora">
                                    {msg.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="ta-msg is-agent">
                                <div className="ta-bolha ta-digitando">
                                    <i /><i /><i />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="ta-pe">
                        <input
                            type="text"
                            placeholder="Escreva como se fosse o cliente…"
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSendMessage(); }}
                            disabled={isTyping}
                        />
                        <button type="button" className="ta-enviar" onClick={handleSendMessage}
                            disabled={!inputValue.trim() || isTyping} aria-label="Enviar mensagem">
                            <LuSend size={14} />
                        </button>
                    </div>
                </>
            )}
        </aside>
    );
};
